import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MapPin, Clock, ShieldCheck, Plane, CreditCard } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useLocalizedPath } from "../i18n/langPath";
import { SEO_CONTENT } from "../i18n/homeContentI18n";

const ROUTES = [
  { slug: "flughafentaxi-luzern", de: "Flughafentaxi Luzern", en: "Airport Taxi Lucerne" },
  { slug: "flughafentaxi-bern", de: "Flughafentaxi Bern", en: "Airport Taxi Bern" },
  { slug: "flughafentaxi-basel", de: "Flughafentaxi Basel", en: "Airport Taxi Basel" },
  { slug: "flughafentaxi-zermatt", de: "Flughafentaxi Zermatt", en: "Airport Taxi Zermatt" },
  { slug: "flughafentaxi-interlaken", de: "Flughafentaxi Interlaken", en: "Airport Taxi Interlaken" },
  { slug: "flughafentaxi-davos", de: "Flughafentaxi Davos", en: "Airport Taxi Davos" },
  { slug: "flughafentaxi-st-moritz", de: "Flughafentaxi St. Moritz", en: "Airport Taxi St. Moritz" },
  { slug: "flughafentaxi-lugano", de: "Flughafentaxi Lugano", en: "Airport Taxi Lugano" },
];

const COPY_DE = {
  h2a: "Ihr Flughafentransfer ab Flughafen Zürich",
  p1: "Zuerich Airport Taxi ist Ihr spezialisierter Anbieter für den Flughafentransfer und das Flughafentaxi am Flughafen Zürich (ZRH). Wir bringen Sie pünktlich, sicher und komfortabel in die ganze Schweiz – zu klaren Festpreisen und mit professionellen, deutschsprachigen Chauffeuren. Ob Geschäftsreise, Familienurlaub oder Skiferien: Ihr privater Transfer ab Zürich Airport ist in unter einer Minute gebucht.",
  h2b: "Flughafentaxi Zürich zu Festpreisen – ohne versteckte Kosten",
  p2: "Anders als beim gewöhnlichen Taxi am Flughafen Zürich zahlen Sie bei uns einen garantierten Festpreis, den Sie schon vor der Buchung sehen. Kein Taxameter, keine Überraschungen. Dank kostenloser Flugverfolgung passen wir die Abholzeit automatisch an Ihre tatsächliche Landung an – auch bei Verspätung warten wir kostenlos auf Sie.",
  h2c: "Beliebte Transferstrecken ab Zürich Airport",
  h2d: "Warum Zuerich Airport Taxi?",
  feats: [
    "Garantierte Festpreise ab CHF 70",
    "Kostenlose Flugverfolgung & Wartezeit",
    "Professionelle Chauffeure, moderne Mercedes-Flotte",
    "Transfers in 6 Länder: Schweiz, Deutschland, Italien, Österreich, Frankreich, Liechtenstein",
    "24/7 verfügbar – auch nachts & an Feiertagen",
    "Bezahlung per Karte, Apple Pay, Google Pay oder bar",
  ],
  cta_prices: "Alle Preise ansehen",
  cta_cities: "Alle Städte & Strecken",
};

const FEAT_ICONS = [CreditCard, Plane, ShieldCheck, MapPin, Clock, CheckCircle2];

export default function SeoContent() {
  const { lang } = useLang();
  const lp = useLocalizedPath();
  const c = lang === "de" ? COPY_DE : (SEO_CONTENT[lang] || SEO_CONTENT.en);
  const rl = lang === "de" ? "de" : "en";

  return (
    <section className="bg-white py-16 md:py-24 border-t border-neutral-100" data-testid="seo-content" aria-label="Flughafentransfer Zürich Information">
      <div className="max-w-4xl mx-auto px-5">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 tracking-tight">{c.h2a}</h2>
        <p className="mt-5 text-neutral-600 leading-relaxed text-base md:text-lg">{c.p1}</p>

        <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 mt-12 tracking-tight">{c.h2b}</h2>
        <p className="mt-4 text-neutral-600 leading-relaxed text-base md:text-lg">{c.p2}</p>

        <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 mt-12 tracking-tight">{c.h2d}</h2>
        <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
          {c.feats.map((f, i) => {
            const Icon = FEAT_ICONS[i % FEAT_ICONS.length];
            return (
              <li key={i} className="flex items-start gap-3 text-neutral-700">
                <span className="mt-0.5 w-8 h-8 rounded-full bg-[#F5B400]/15 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#c9a227]" />
                </span>
                <span className="text-[15px] md:text-base">{f}</span>
              </li>
            );
          })}
        </ul>

        <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 mt-12 tracking-tight">{c.h2c}</h2>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ROUTES.map((r) => (
            <Link
              key={r.slug}
              to={lp(`/${r.slug}`)}
              data-testid={`seo-route-${r.slug}`}
              className="group rounded-xl border border-neutral-200 bg-white px-4 py-3.5 text-sm font-semibold text-neutral-800 hover:border-[#F5B400] hover:bg-[#F5B400]/5 transition-colors flex items-center gap-2"
            >
              <MapPin className="w-4 h-4 text-[#F5B400] shrink-0" />
              <span className="truncate">{r[rl]}</span>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link to={lp("/preise")} data-testid="seo-cta-prices" className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#F5B400] hover:bg-[#D99A00] text-neutral-900 font-bold transition-colors">
            {c.cta_prices}
          </Link>
          <Link to={lp("/staedte")} data-testid="seo-cta-cities" className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-neutral-300 text-neutral-800 font-semibold hover:bg-neutral-50 transition-colors">
            {c.cta_cities}
          </Link>
        </div>
      </div>
    </section>
  );
}
